import { Dialog } from '@madpaws/design-system';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

// import { MobileVerificationForm } from './MobileVerificationForm';
import { FORM_IDS } from '~/common/constants/app';
import { useAuthenticationContext } from '~/components/AuthenticationProvider/useAuthenticationContext';
import { DOM_CONTAINER_ID } from '~/components/constants';
import { AuthApi } from '~/services/auth/authApi';
import { sentryException } from '~/utils/sentryHelpers';

import { TermsConfirmationForm } from '../TermsConfirmationForm/TermsConfirmationForm';
import { getSocialLoginArgs } from '../helpers/getSocialLoginArgs';

import type { SocialSignUpProps } from '../AuthDialog/AuthDialog';
import type { AuthMethods } from '../constants';
import type { OnSignUpSuccessHandler } from '../types';
import type { ReactElement } from 'react';
import type {
  LoginSocialExistingUserRestResponse,
  LoginSocialNewUserRestResponse,
  SocialLoginArgs,
} from '~/services/auth/types';

export type Props = {
  onSignUpSuccess: OnSignUpSuccessHandler;
  signUpData: (LoginSocialNewUserRestResponse & { authMethod: AuthMethods }) | null;
  termsFormError?: string | null;
};

enum GoogleAuthFormStep {
  Terms = 'terms',
  Mobile = 'mobile',
}

// Exported for use in input validation
export const SocialSignUpDialog = ({
  onSignUpSuccess,
  signUpData,
  termsFormError,
}: Props): ReactElement => {
  const [step, setStep] = useState<GoogleAuthFormStep>(GoogleAuthFormStep.Terms);
  const [isTermsFormSubmitting, setIsTermsFormSubmitting] = useState<boolean>(false);

  const router = useRouter();
  const {
    route: routerRoute,
    query: { refSource, referralCode },
  } = router;
  // const [isNewUser, setIsNewUser] = useState<boolean>(false);

  const { isSocialSignUpDialogOpen, setIsSocialSignUpDialogOpen } = useAuthenticationContext();

  useEffect(() => {
    if (signUpData) {
      const {
        // isUserPhoneNumberVerified,
        userNeedsToAcceptTerms,
      } = signUpData;
      // if user needs to accept terms, show TermsConfirmationForm
      // regardless the value of isUserPhoneNumberVerified
      if (userNeedsToAcceptTerms) {
        setStep(GoogleAuthFormStep.Terms);
        // setIsNewUser(true);

        return;
      }
      // if user don't need to accept terms and user has unverified phone number
      // show MobileVerificationForm
      // if (isUserPhoneNumberVerified === false) {
      //   setStep(GoogleAuthFormStep.Mobile);
      // }
    }
  }, [signUpData]);

  const onDialogClose = (): void => {
    setIsSocialSignUpDialogOpen(false);
  };

  const sendSentryException = (error: unknown, socialLoginArgs: SocialLoginArgs): void => {
    sentryException(
      error,
      `signInWithSocial API failed, socialLoginArgs: ${JSON.stringify(socialLoginArgs, null, 2)}`
    );
  };

  const onSocialSignUpFormSubmit = async (data: SocialSignUpProps): Promise<void> => {
    setIsTermsFormSubmitting(true);

    const extraArgs = {
      one_tap: routerRoute,
      refSource: (refSource as string) ?? null,
      referralCode: (referralCode as string) ?? null,
    };

    const socialLoginArgs: SocialLoginArgs = (await getSocialLoginArgs(
      data.socialToken,
      signUpData?.authMethod as AuthMethods,
      data,
      extraArgs
    )) as SocialLoginArgs;

    try {
      const response = await AuthApi.loginWithSocials(
        socialLoginArgs,
        signUpData?.authMethod as AuthMethods
      );

      const res = response as LoginSocialExistingUserRestResponse;

      onSignUpSuccess(
        res,
        { email: res.email, userId: res.userId },
        signUpData?.authMethod as AuthMethods,
        { receiveSpecialOffers: data.receiveSpecialOffers ?? false }
      );

      setIsTermsFormSubmitting(false);
      setIsSocialSignUpDialogOpen(false);
    } catch (error) {
      sendSentryException(error, socialLoginArgs);
    }
  };

  return (
    <Dialog
      domContainerId={DOM_CONTAINER_ID}
      formId={FORM_IDS.SOCIAL_SIGN_UP_FORM}
      heading="Welcome to Mad Paws"
      isOpen={isSocialSignUpDialogOpen}
      onRequestClose={onDialogClose}
    >
      {step === GoogleAuthFormStep.Terms && (
        <TermsConfirmationForm
          data={signUpData}
          formError={termsFormError ?? null}
          isSubmitting={isTermsFormSubmitting}
          onCancel={onDialogClose}
          onSubmit={onSocialSignUpFormSubmit}
        />
      )}
      {/* {step === GoogleAuthFormStep.Mobile && (
        <MobileVerificationForm
        data={data}
        isNewUser={isNewUser}
        onSuccessLogin={onSuccessLogin} />
      )} */}
    </Dialog>
  );
};
